import React, { useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Container,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LogoutIcon from '@mui/icons-material/Logout';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ContactsIcon from '@mui/icons-material/Contacts';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import SyncIcon from '@mui/icons-material/Sync';
import ShieldIcon from '@mui/icons-material/Shield';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import StorefrontIcon from '@mui/icons-material/Storefront';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND } from '../../config/brand';

// ============================================================================
// SUPERADMIN ORTAK LAYOUT (responsive)
// ============================================================================
// Daha önce her superadmin sayfası kendi sidebar/header kopyasını taşıyordu
// (menü listeleri birbirinden farklılaşmıştı) ve hiçbiri mobil uyumlu değildi.
// Bu bileşen tek kaynak:
//  - Masaüstü (md+): sabit, daraltılabilir sidebar (240px / 70px) + AppBar.
//  - Mobil (<md): sidebar YOK; AppBar'daki hamburger geçici Drawer menü açar.
//  - İçerik: AppBar altında, responsive padding'li, yatay taşmaya kapalı alan.
// Kullanım:
//   <SuperAdminLayout title="Personeller" icon={<PeopleAltIcon />}>...</SuperAdminLayout>
// ============================================================================

const EXPANDED_WIDTH = 240;
const COLLAPSED_WIDTH = 70;

/**
 * Tüm superadmin sayfalarında ortak menü (tek kaynak).
 * roles: öğeyi görebilecek roller — bayi (dealer) yalnızca Şirketler'i görür.
 * Menü filtresi UI içindir; rota koruması App.tsx RequireRole + backend guard'larındadır.
 */
const MENU_ITEMS: Array<{ label: string; path: string; icon: React.ReactElement; roles: string[] }> = [
  { label: 'Şirketler', path: '/superadmin', icon: <BusinessIcon />, roles: ['superadmin', 'dealer'] },
  { label: 'Bayiler', path: '/superadmin/dealers', icon: <StorefrontIcon />, roles: ['superadmin'] },
  { label: 'Personeller', path: '/superadmin/personnel', icon: <PeopleAltIcon />, roles: ['superadmin'] },
  { label: 'İletişim Listesi', path: '/superadmin/contacts', icon: <ContactsIcon />, roles: ['superadmin'] },
  { label: 'Silme Talepleri', path: '/superadmin/deletion-requests', icon: <DeleteSweepIcon />, roles: ['superadmin'] },
  { label: 'User Migration', path: '/superadmin/user-migration', icon: <SyncIcon />, roles: ['superadmin'] },
  { label: 'Yetki Taşıma', path: '/superadmin/permission-migration', icon: <ShieldIcon />, roles: ['superadmin'] },
  { label: 'API Güvenlik', path: '/superadmin/middleware-monitor', icon: <ShieldIcon />, roles: ['superadmin'] },
  { label: 'Mail Raporlari', path: '/superadmin/mail-reports', icon: <MarkEmailReadIcon />, roles: ['superadmin'] },
];

const SidebarMenuItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'isCollapsed',
})<{ active?: boolean; isCollapsed?: boolean }>(({ theme, active, isCollapsed }) => ({
  padding: theme.spacing(1.25, isCollapsed ? 2 : 3),
  marginBottom: theme.spacing(0.5),
  cursor: 'pointer',
  borderLeft: active ? '4px solid white' : '4px solid transparent',
  backgroundColor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
  color: 'white',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
}));

interface SuperAdminLayoutProps {
  /** AppBar'da görünen sayfa başlığı. */
  title: string;
  /** Başlığın solunda görünen ikon. */
  icon?: React.ReactNode;
  /** İçerik Container genişliği; false → Container kullanılmaz (tam genişlik). */
  maxWidth?: 'lg' | 'xl' | false;
  /** İçeriğin kendi scroll'unu yönettiği durumlar için padding'i kaldırır. */
  disablePadding?: boolean;
  children: React.ReactNode;
}

/** Sidebar/Drawer'ın ortak iç içeriği (logo + menü + footer). */
const SidebarContent: React.FC<{
  isCollapsed: boolean;
  onNavigate: (path: string) => void;
}> = ({ isCollapsed, onNavigate }) => {
  const location = useLocation();
  const { userDetails, isDealer } = useAuth();

  const role = (userDetails?.role || '').toLowerCase();
  const visibleMenuItems = MENU_ITEMS.filter((item) => item.roles.includes(role));

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#1976d2',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Box
          component="img"
          src={BRAND.logos.main}
          alt={`${BRAND.name} Logo`}
          sx={{
            width: isCollapsed ? 40 : 70,
            height: isCollapsed ? 40 : 70,
            borderRadius: '10px',
            mb: isCollapsed ? 0 : 1,
            transition: 'all 0.3s ease',
          }}
        />
        {!isCollapsed && (
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
            {isDealer ? 'Bayi Paneli' : 'Süper Admin'}
          </Typography>
        )}
        {!isCollapsed && isDealer && userDetails?.firstName && (
          <Typography variant="caption" sx={{ color: 'white', opacity: 0.85 }} noWrap>
            {`${userDetails.firstName} ${userDetails.lastName || ''}`.trim()}
          </Typography>
        )}
      </Box>

      <List
        sx={{
          flexGrow: 1,
          py: 2,
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '2px',
          },
        }}
      >
        {visibleMenuItems.map((item) => (
          <SidebarMenuItem
            key={item.path}
            active={location.pathname === item.path}
            isCollapsed={isCollapsed}
            onClick={() => onNavigate(item.path)}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: isCollapsed ? 0 : 40 }}>
              {item.icon}
            </ListItemIcon>
            {!isCollapsed && (
              <ListItemText primary={item.label} primaryTypographyProps={{ noWrap: true }} />
            )}
          </SidebarMenuItem>
        ))}
      </List>

      <Box
        sx={{
          p: isCollapsed ? 1 : 2,
          textAlign: 'center',
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Typography variant="body2" sx={{ color: 'white', opacity: 0.8, whiteSpace: 'nowrap' }}>
          {isCollapsed ? '©' : `© ${BRAND.name} ${new Date().getFullYear()}`}
        </Typography>
      </Box>
    </Box>
  );
};

const SuperAdminLayout: React.FC<SuperAdminLayoutProps> = ({
  title,
  icon,
  maxWidth = 'xl',
  disablePadding = false,
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isNarrowDesktop = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();

  // Masaüstü: dar ekranlarda (md-lg) varsayılan daraltılmış başlar
  const [collapsed, setCollapsed] = useState<boolean>(() => isNarrowDesktop);
  // Mobil: geçici drawer
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const sidebarWidth = isMobile ? 0 : collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  const handleNavigate = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
      setAnchorEl(null);
      navigate('/login');
    } catch (error) {
      console.error('Çıkış yapılırken hata oluştu:', error);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: 'background.default' }}>
      {/* Masaüstü: sabit sidebar */}
      {!isMobile && (
        <Box
          sx={{
            width: sidebarWidth,
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            zIndex: 1200,
            boxShadow: '0 0 10px rgba(0,0,0,0.3)',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <SidebarContent isCollapsed={collapsed} onNavigate={handleNavigate} />
          <IconButton
            onClick={() => setCollapsed((p) => !p)}
            size="small"
            sx={{
              position: 'absolute',
              right: -12,
              top: 55,
              bgcolor: '#1976d2',
              color: 'white',
              border: '2px solid white',
              boxShadow: '0 0 5px rgba(0,0,0,0.2)',
              zIndex: 1201,
              '&:hover': { bgcolor: '#1976d2', opacity: 0.9 },
            }}
          >
            {collapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        </Box>
      )}

      {/* Mobil: geçici drawer menü */}
      {isMobile && (
        <Drawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ sx: { width: EXPANDED_WIDTH, border: 'none' } }}
        >
          <SidebarContent isCollapsed={false} onNavigate={handleNavigate} />
        </Drawer>
      )}

      {/* Sağ taraf: AppBar + içerik */}
      <Box sx={{ flex: 1, minWidth: 0, ml: `${sidebarWidth}px` }}>
        <AppBar
          elevation={0}
          sx={{
            position: 'fixed',
            bgcolor: '#1976d2',
            color: 'white',
            boxShadow: '0px 1px 4px rgba(0,0,0,0.1)',
            width: `calc(100% - ${sidebarWidth}px)`,
            ml: `${sidebarWidth}px`,
            zIndex: 1100,
            transition: theme.transitions.create(['width', 'margin-left'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <Toolbar sx={{ gap: 1, minHeight: { xs: 56, md: 64 } }}>
            {isMobile && (
              <IconButton color="inherit" edge="start" onClick={() => setDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              {icon}
              <Typography
                variant={isMobile ? 'h6' : 'h5'}
                component="h1"
                noWrap
                sx={{ fontWeight: 'bold' }}
              >
                {title}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Avatar
              sx={{
                bgcolor: 'primary.dark',
                cursor: 'pointer',
                width: { xs: 34, md: 40 },
                height: { xs: 34, md: 40 },
              }}
              onClick={(e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget)}
            >
              {currentUser?.email?.charAt(0).toUpperCase()}
            </Avatar>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              PaperProps={{ sx: { width: 200, mt: 1 } }}
            >
              <MenuItem onClick={handleLogout}>
                <LogoutIcon sx={{ mr: 1 }} />
                Çıkış Yap
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* İçerik */}
        <Box
          sx={{
            mt: { xs: 7, md: 8 },
            p: disablePadding ? 0 : { xs: 1.5, sm: 2, md: 3 },
            minWidth: 0,
            overflowX: 'hidden',
          }}
        >
          {maxWidth === false ? (
            children
          ) : (
            <Container maxWidth={maxWidth} disableGutters sx={{ minWidth: 0 }}>
              {children}
            </Container>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default SuperAdminLayout;
